import { useEffect, useState } from "react";

interface GalleryProps {
  images: string[];
  title: string;
}

export default function Gallery({ images, title }: GalleryProps) {
  const [active, setActive] = useState(0);
  const [lightbox, setLightbox] = useState(false);

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setLightbox(false);
      if (e.key === "ArrowRight") setActive((i) => (i + 1) % images.length);
      if (e.key === "ArrowLeft") setActive((i) => (i - 1 + images.length) % images.length);
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [lightbox, images.length]);

  if (images.length === 0) return null;

  return (
    <div>
      <button
        type="button"
        onClick={() => setLightbox(true)}
        className="group relative block h-72 w-full cursor-zoom-in overflow-hidden rounded-lg md:h-[520px]"
      >
        <img
          src={images[active]}
          alt={`${title} - 张津瑜作品图集 ${active + 1}`}
          title={`${title} + 张津瑜作品`}
          className="h-full w-full object-cover object-top"
        />
        <span className="absolute bottom-4 right-4 flex items-center gap-1.5 rounded-md bg-primary-950/70 px-3 py-1.5 text-xs text-background-50 backdrop-blur">
          <i className="ri-zoom-in-line text-sm" />
          点击查看大图
        </span>
      </button>

      {images.length > 1 && (
        <div className="mt-4 flex flex-wrap gap-3">
          {images.map((img, index) => (
            <button
              key={img}
              type="button"
              onClick={() => setActive(index)}
              className={`h-16 w-24 shrink-0 cursor-pointer overflow-hidden rounded-md border-2 transition-colors md:h-20 md:w-28 ${
                index === active ? "border-accent-500" : "border-transparent opacity-70 hover:opacity-100"
              }`}
            >
              <img
                src={img}
                alt={`${title} - 缩略图 ${index + 1}`}
                title={`${title} + 张津瑜作品`}
                className="h-full w-full object-cover object-top"
              />
            </button>
          ))}
        </div>
      )}

      {lightbox && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-primary-950/90 p-4 animate-fade-in"
          onClick={() => setLightbox(false)}
        >
          <button
            type="button"
            aria-label="关闭预览"
            onClick={() => setLightbox(false)}
            className="absolute right-5 top-5 flex h-11 w-11 items-center justify-center rounded-full bg-background-50/15 text-background-50 transition-colors hover:bg-background-50/30"
          >
            <i className="ri-close-line text-2xl" />
          </button>

          <button
            type="button"
            aria-label="上一张"
            onClick={(e) => {
              e.stopPropagation();
              setActive((i) => (i - 1 + images.length) % images.length);
            }}
            className="absolute left-3 flex h-11 w-11 items-center justify-center rounded-full bg-background-50/15 text-background-50 transition-colors hover:bg-background-50/30 md:left-8"
          >
            <i className="ri-arrow-left-s-line text-2xl" />
          </button>

          <img
            src={images[active]}
            alt={`${title} - 大图预览 ${active + 1}`}
            title={`${title} + 张津瑜作品`}
            onClick={(e) => e.stopPropagation()}
            className="max-h-[85vh] max-w-full rounded-lg object-contain"
          />

          <button
            type="button"
            aria-label="下一张"
            onClick={(e) => {
              e.stopPropagation();
              setActive((i) => (i + 1) % images.length);
            }}
            className="absolute right-3 flex h-11 w-11 items-center justify-center rounded-full bg-background-50/15 text-background-50 transition-colors hover:bg-background-50/30 md:right-8"
          >
            <i className="ri-arrow-right-s-line text-2xl" />
          </button>

          <span className="absolute bottom-6 left-1/2 -translate-x-1/2 rounded-full bg-background-50/15 px-4 py-1.5 text-sm text-background-50">
            {active + 1} / {images.length}
          </span>
        </div>
      )}
    </div>
  );
}